import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Check, Sparkles, Smartphone, Paintbrush, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { siteData, getWhatsAppUrl } from '../data/mock';

export const Templates = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="templates" ref={ref} className="py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-1/4 left-0 w-full h-[500px] bg-gradient-to-r from-vibrant-red/5 via-vibrant-purple/5 to-vibrant-blue/5 -skew-y-6 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-vibrant-red uppercase bg-red-50 rounded-full border border-red-100">
            Showcase
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-slate-900 mb-6 font-outfit">
            Stunning <span className="text-transparent bg-clip-text bg-gradient-to-r from-vibrant-red to-vibrant-orange">Templates</span>
          </h2>
          <p className="text-lg text-slate-600">
            Browse our collection of handcrafted designs, optimized for mobile and tailored for Indian weddings.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {siteData.templates.map((template, index) => (
            <div
              key={template.id}
              className={`group relative bg-white rounded-[2rem] overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 transition-all duration-500 hover:shadow-2xl hover:shadow-vibrant-blue/10 hover:-translate-y-2 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Image Container with Scroll Effect */}
              <div className="relative h-[400px] overflow-hidden bg-slate-100 group-hover:h-[380px] transition-all duration-500">
                <div className="absolute inset-x-0 bottom-0 top-0 overflow-y-auto no-scrollbar scroll-smooth pointer-events-none group-hover:pointer-events-auto">
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full object-cover transition-transform duration-[3s] ease-linear group-hover:translate-y-[-20%]"
                  />
                </div>

                {/* Live Preview Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                  <Button
                    className="rounded-full px-6 py-6 bg-white text-slate-900 hover:bg-white hover:scale-105"
                    onClick={() => window.open(template.demoUrl, '_blank')}
                  >
                    Live Preview <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-8">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 font-outfit mb-1">{template.name}</h3>
                    <div className="flex gap-2">
                      <Badge variant="secondary" className="bg-blue-50 text-vibrant-blue hover:bg-blue-100"><Smartphone className="w-3 h-3 mr-1" /> Mobile First</Badge>
                      <Badge variant="secondary" className="bg-green-50 text-vibrant-green hover:bg-green-100">Fast Load</Badge>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg">
                    {template.price}
                  </div>
                </div>

                <ul className="space-y-2 mb-8">
                  {template.features.slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-center text-sm text-slate-600">
                      <Check className="w-4 h-4 mr-2 text-vibrant-green" /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full mb-3 rounded-full bg-gradient-to-r from-vibrant-purple to-vibrant-pink hover:opacity-90 border-0 h-11 font-semibold text-white shadow-lg shadow-purple-200/50"
                  onClick={() => {
                    const message = `Hi, I want to book the ${template.name} template.${template.demoUrl ? `\n\nLink: ${template.demoUrl}` : ''}`;
                    window.open(getWhatsAppUrl(message), '_blank');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Want this Template
                </Button>

                <Button
                  variant="outline"
                  className="w-full rounded-full border-2 border-slate-200 hover:border-vibrant-blue hover:text-vibrant-blue group-hover:bg-blue-50 h-11"
                  onClick={() => window.open(template.demoUrl, '_blank')}
                >
                  View Details
                </Button>
              </div>
            </div>
          ))}

          {/* Custom Design Card */}
          <div
            className={`flex flex-col justify-center items-center p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] text-center text-white shadow-xl shadow-slate-900/20 transition-all duration-500 hover:scale-[1.02] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            style={{ transitionDelay: `${siteData.templates.length * 150}ms` }}
          >
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-6 animate-pulse">
              <Paintbrush className="w-10 h-10 text-vibrant-purple" />
            </div>
            <h3 className="text-2xl font-bold font-outfit mb-4">Need a Custom Design?</h3>
            <p className="text-white/70 mb-8 leading-relaxed">
              We can create a completely unique website tailored to your specific theme and requirements.
            </p>
            <Button
              className="w-full rounded-full bg-gradient-to-r from-vibrant-purple to-vibrant-pink hover:opacity-90 border-0 h-12 text-lg font-semibold"
              onClick={() => window.open(getWhatsAppUrl("Hey Hardik, I want custom designed template for my WedSite , My name is "), '_blank')}
            >
              <Sparkles className="w-5 h-5 mr-2" /> Start Custom Project
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
